// @file Managing and executing visual effects
import { checkOnNextPaintRecursively } from '@@/bits/dom'
import type { Engine, ISourceOptions } from '@tsparticles/engine'
import { throttle } from 'lodash-es'

interface VisualEffectExecuteOptions {
  origin?: { x: number; y: number } | null
}

interface CustomVisualEffect {
  type: 'custom'
  id: string
  /**
   * Function to execute the visual effect, returns control object
   */
  execute: (
    target: HTMLElement,
    options?: VisualEffectExecuteOptions,
  ) => {
    /** Stops the visual effect */
    stop: () => void
    /** Promise that resolves when the visual effect is complete */
    promise: Promise<unknown>
  }
  /** @default 2000 (ms) */
  duration?: number
}

interface TsParticlesVisualEffect {
  type: 'tsParticles'
  id: string
  getOptions: (engine: Engine, options?: VisualEffectExecuteOptions) => Promise<ISourceOptions>
  /** @default 5000 (ms) */
  maxDuration?: number
}

type VisualEffect = CustomVisualEffect | TsParticlesVisualEffect

const MAX_CONCURRENT_EFFECTS = 5 // per effect type

class VfxManager {
  private readonly effects: Map<string, VisualEffect> = new Map()
  private readonly runningControls = new Set<() => void>()
  private readonly runningEffectCounts = new Map<string, number>()

  private addRunningEffect(id: string, stopFunc: () => void): void {
    this.runningControls.add(stopFunc)
    this.runningEffectCounts.set(id, (this.runningEffectCounts.get(id) ?? 0) + 1)
  }

  private removeRunningEffect(id: string, stopFunc: () => void): void {
    this.runningControls.delete(stopFunc)
    const newCount = (this.runningEffectCounts.get(id) ?? 1) - 1
    if (newCount <= 0) {
      this.runningEffectCounts.delete(id)
    } else {
      this.runningEffectCounts.set(id, newCount)
    }
  }

  private getRenderTarget(): HTMLElement {
    const portalTarget = document.getElementById('special-effects-portal-target')
    return portalTarget ?? document.body
  }

  /**
   * Loads visual effects by their IDs dynamically
   * @param ids - Array of effect IDs to load
   * @returns Promise that resolves when all effects are loaded
   */
  public async loadEffects(ids: string[]): Promise<void> {
    try {
      const effects = await Promise.all(
        ids.map(async (id) => {
          const module = await import(`@@/bits/visual_effects/${id}.ts`)
          return module.default
        }),
      )
      effects.forEach((effect) => this.effects.set(effect.id, effect))
    } catch {
      // Do nothing for now
    }
  }

  /**
   * Executes a visual effect by its ID with throttling (500ms)
   * @param id - ID of the effect to execute
   * @param options - Additional options for the effect
   * @returns Promise that resolves when the effect execution is complete
   */
  public executeEffect = throttle(async (id: string, options?: VisualEffectExecuteOptions): Promise<void> => {
    const currentCount = this.runningEffectCounts.get(id) ?? 0
    if (currentCount >= MAX_CONCURRENT_EFFECTS) return

    let effect = this.effects.get(id)
    if (effect == null) {
      await this.loadEffects([id])
      effect = this.effects.get(id)
    }
    if (effect != null) {
      if (effect.type === 'tsParticles') {
        await this.executeTsParticlesEffect(effect, options)
      } else {
        await this.executeCustomEffect(effect, options)
      }
    }
  }, 500)

  private async executeCustomEffect(effect: CustomVisualEffect, options?: VisualEffectExecuteOptions): Promise<void> {
    const target = this.getRenderTarget()
    const control = effect.execute(target, options)

    this.addRunningEffect(effect.id, control.stop)

    // Stop effect after duration
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        control.stop()
        resolve()
      }, effect?.duration ?? 2000)
    })

    try {
      // Wait for effect to complete, but not more than 50ms in case it's stuck
      const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 50))
      await Promise.race([control.promise, timeoutPromise])
    } finally {
      this.removeRunningEffect(effect.id, control.stop)
    }
  }

  private async executeTsParticlesEffect(
    effect: TsParticlesVisualEffect,
    options?: VisualEffectExecuteOptions,
  ): Promise<void> {
    // Always lazy load tsParticles engine
    const { tsParticles } = await import('@tsparticles/engine')
    const target = this.getRenderTarget()

    const instance = await tsParticles.load({
      id: effect.id,
      element: target,
      options: {
        ...(await effect.getOptions(tsParticles, options)),
        autoPlay: true,
      },
    })

    const stop = (): void => {
      instance?.stop()
      instance?.destroy(true)
    }
    this.addRunningEffect(effect.id, stop)

    try {
      // Wait for effect to complete, but not more than `maxDuration` in case it's stuck
      const timeoutPromise = new Promise((resolve) => setTimeout(resolve, effect.maxDuration ?? 5000))
      const runningPromise = checkOnNextPaintRecursively(() => instance == null || instance.destroyed)
      await Promise.race([runningPromise, timeoutPromise])
    } finally {
      stop()
      this.removeRunningEffect(effect.id, stop)
    }
  }

  /**
   * Stops all currently running visual effects
   */
  public stopAllEffects(): void {
    for (const stop of this.runningControls) {
      stop()
    }
    this.runningControls.clear()
    this.runningEffectCounts.clear()
  }
}

export const vfxManager = new VfxManager()
export type { VisualEffect, VisualEffectExecuteOptions }
